import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '../../services/admin';
import { Bus } from '../../../../shared/models/bus';
import { BusFormData } from '../../../../shared/models/bus-form-data';
import { BusForm } from '../bus-form/bus-form';
import { Observable, of } from 'rxjs';
import { PermissionService } from '../../../../core/authorization/permission';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-buses',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatButtonModule, MatIconModule, MatSortModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, FormsModule],
  templateUrl: './buses.html',
  styleUrl: './buses.css'
})
export class Buses implements OnInit, AfterViewInit {
  	dataSource       : MatTableDataSource<Bus> = new MatTableDataSource<Bus>([]);
    displayedColumns : string[] = ['name', 'licensePlate', 'status', 'partnerName', 'routeName', 'entityName', 'createdAt', 'actions'];
    canCreateBus$   : Observable<boolean>;
    canEditBus$     : Observable<boolean>;
    canDeleteBus$   : Observable<boolean>;

    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(private adminService: AdminService, private dialog: MatDialog, private permissionService: PermissionService) {
        this.canCreateBus$ = this.permissionService.hasPermission('buses.create');
        this.canEditBus$   = this.permissionService.hasPermission('buses.update');
        this.canDeleteBus$ = of(false);

        this.dataSource.filterPredicate = (data: Bus, filter: string) => {
            const searchString      = filter.trim().toLowerCase();
            const nameMatch         = data.name.toLowerCase().includes(searchString);
            const licensePlateMatch = data.licensePlate.toLowerCase().includes(searchString);
            const partnerNameMatch  = data.partnerName.toLocaleLowerCase().includes(searchString);
            const routeNameMatch    = data.routeName.toLocaleLowerCase().includes(searchString);
            const entityNameMatch   = data.entityName.toLocaleLowerCase().includes(searchString);

            return nameMatch || licensePlateMatch || partnerNameMatch || routeNameMatch || entityNameMatch;
        };
    }

    ngOnInit(): void {
        this.loadBuses();
    }

    ngAfterViewInit(): void {
        this.dataSource.sort      = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    async loadBuses(): Promise<void> {
        const result = await this.adminService.getAllBuses();

        if(result) {
            this.dataSource.data = result;
        }
    }

    applyFilter(event: Event): void {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();

		if(this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

    async onCreateBus(): Promise<void> {
        const dialogRef = this.dialog.open(BusForm, {
            width: '600px',
            disableClose: true,
            data: null
        });

        dialogRef.afterClosed().subscribe(async (result: BusFormData | undefined) => {
            if(result) {
                const success = await this.adminService.createBus({
                    name        : result.name,
                    licensePlate: result.licensePlate,
                    status      : result.status,
                    partnerId   : result.partnerId,
                    routeId     : result.routeId,
                    entityId    : result.entityId
                });

                if(success) {
					this.loadBuses();
                }
            }
        });
    }

    async onEditBus(bus: Bus): Promise<void> {
        const formData: BusFormData = {
            id          : bus.id,
            name        : bus.name,
            licensePlate: bus.licensePlate,
            status      : bus.status,
            partnerId   : bus.partnerId,
            routeId     : bus.routeId,
            entityId    : bus.entityId
        };

        const dialogRef = this.dialog.open(BusForm, {
			width: '600px',
			disableClose: true,
			data: formData
        });

        dialogRef.afterClosed().subscribe(async (result: BusFormData | undefined) => {
            if(result) {
                const busId = result.id;

                const updatePayload: Partial<Omit<BusFormData, 'id'>> = {
                    name        : result.name,
                    licensePlate: result.licensePlate,
                    status      : result.status,
                    partnerId   : result.partnerId,
                    routeId     : result.routeId,
                    entityId    : result.entityId
                };

                const success = await this.adminService.updateBus(busId!, updatePayload);

                if(success) {
                    this.loadBuses();
                }
            }
        });
    }


    async onDeleteBus(id: string): Promise<void> {
        if(window.confirm('¿Estás seguro de que quieres eliminar este bus? Esta acción es irreversible.')) {
            const success = await this.adminService.deleteBus(id);
            
			if(success) {
                this.loadBuses();
            }
        }
    }
}
